/*
 * DSD Membership Registry - p11dz146
 * Membership contract and Phase 5 governance bridge for canonical social objects.
 * Defines membership record shapes, roles, states, scopes, lifecycle, and
 * future Universe/Galaxy/Event/Chat readiness. Platform DSD+ entitlement
 * enforcement is delegated to Shopify tag verification and Supabase-derived
 * entitlement state through LaunchSocialActivation.
 */
(function(){
  'use strict';
  if (window.DSDMembershipRegistry && window.DSDMembershipRegistry.version) return;

  var VERSION = 'p11dz146-membership-registry-v1';
  var OWNER = 'MembershipRegistry';
  var ENTITLEMENT_GATE = 'active-via-shopify-tags-and-supabase-entitlement';
  var REQUIRED_DSD_PLUS_TAGS = ['CM-6185 Membership: Active', 'CM-6185 Tier: DSD-Plus-Monthly'];

  function claim(surface, meta){
    try {
      if (window.DSDRegistry && typeof window.DSDRegistry.claim === 'function') {
        window.DSDRegistry.claim(surface, OWNER, Object.assign({ version: VERSION }, meta || {}));
      }
    } catch(e) {}
  }

  function exists(name){ return !!window[name]; }
  function arrayify(value){
    if (Array.isArray(value)) return value;
    if (value === undefined || value === null || value === '') return [];
    return [value];
  }
  function slug(value){
    return String(value || '').trim().toLowerCase().replace(/[^a-z0-9]+/g,'-').replace(/^-+|-+$/g,'');
  }
  function unique(values){
    var seen = {};
    return arrayify(values).map(slug).filter(function(v){
      if (!v || seen[v]) return false;
      seen[v] = true;
      return true;
    });
  }

  var entityTypes = {
    platform: { key: 'platform', label: 'Platform', userCreatable: false },
    profile: { key: 'profile', label: 'Profile', userCreatable: false },
    universe: { key: 'universe', label: 'Universe', userCreatable: 'future-approval-required' },
    galaxy: { key: 'galaxy', label: 'Galaxy', userCreatable: 'future-trust-gated' },
    event: { key: 'event', label: 'Event', userCreatable: 'future-trust-gated' },
    conversation: { key: 'conversation', label: 'Conversation / Chat', userCreatable: 'future-scope-controlled' },
    storyGroup: { key: 'storyGroup', label: 'Story Group', userCreatable: 'future-scope-controlled' }
  };

  var membershipRoles = {
    founder: { key: 'founder', rank: 100, label: 'Founder', canInvite: true, canAssignAdmins: true, canAssignModerators: true, canTransferOwnership: true },
    admin: { key: 'admin', rank: 80, label: 'Admin', canInvite: true, canAssignModerators: true, canCreateChildEntities: true },
    moderator: { key: 'moderator', rank: 60, label: 'Moderator', canModerate: true, canInvite: 'configurable' },
    trustedMember: { key: 'trustedMember', rank: 35, label: 'Trusted Member', canCreateChildEntities: 'configurable' },
    member: { key: 'member', rank: 20, label: 'Member' },
    applicant: { key: 'applicant', rank: 5, label: 'Applicant' },
    invited: { key: 'invited', rank: 4, label: 'Invited' },
    muted: { key: 'muted', rank: 1, label: 'Muted' },
    removed: { key: 'removed', rank: 0, label: 'Removed' },
    banned: { key: 'banned', rank: -10, label: 'Banned' }
  };

  var membershipStates = {
    active: { key: 'active', canParticipate: true, visibleInCounts: true },
    pending: { key: 'pending', canParticipate: false, visibleInCounts: false },
    invited: { key: 'invited', canParticipate: false, visibleInCounts: false },
    muted: { key: 'muted', canParticipate: 'limited', visibleInCounts: true },
    suspended: { key: 'suspended', canParticipate: false, visibleInCounts: true },
    removed: { key: 'removed', canParticipate: false, visibleInCounts: false },
    banned: { key: 'banned', canParticipate: false, visibleInCounts: false },
    left: { key: 'left', canParticipate: false, visibleInCounts: false },
    archived: { key: 'archived', canParticipate: false, visibleInCounts: false }
  };

  var creationModes = {
    open: { key: 'open', label: 'Open Creation', approvalRequired: false },
    trustedMembers: { key: 'trustedMembers', label: 'Trusted Members', approvalRequired: false, trustRequired: true },
    adminsOnly: { key: 'adminsOnly', label: 'Admins Only', approvalRequired: false, roleRequired: ['founder','admin'] },
    approvalRequired: { key: 'approvalRequired', label: 'Founder/Admin Approval Required', approvalRequired: true },
    disabled: { key: 'disabled', label: 'Disabled', approvalRequired: true, disabled: true }
  };

  var childCreationAuthority = {
    universe: {
      canCreateChildTypes: ['galaxy','event','storyGroup'],
      defaultGalaxyCreationMode: 'approvalRequired',
      rolesAllowedToCreateGalaxies: ['founder','admin'],
      rolesAllowedToApproveGalaxies: ['founder','admin'],
      galaxySoftLimitPolicy: 'health-and-trust-based-not-hardcoded'
    },
    galaxy: {
      canCreateChildTypes: ['event','conversation','storyGroup'],
      rolesAllowedToCreateEvents: ['founder','admin','moderator'],
      rolesAllowedToCreateChats: ['founder','admin'],
      rolesAllowedToCreateStories: ['founder','admin','moderator','trustedMember']
    },
    event: {
      canCreateChildTypes: ['conversation','storyGroup'],
      rolesAllowedToCreateChats: ['founder','admin','moderator'],
      rolesAllowedToCreateStories: ['founder','admin','moderator']
    }
  };

  var membershipDoctrine = [
    'Membership is a canonical relationship between a user identity and a social entity; it is not owned by a renderer, feed, modal, or route.',
    'Universes, galaxies, events, story groups, and chats must use one membership registry instead of separate membership systems.',
    'Every user-created social layer must have explicit Founder, Admin, Moderator, and Member authority definitions.',
    'Universe founders may invite users and assign admins/moderators; admins and configured moderators may create or approve galaxies depending on universe creation mode.',
    'Galaxy founders may invite users and assign galaxy admins/moderators, but cannot override platform moderation or global bans.',
    'Galaxy limits should be governed by health/trust/abuse signals rather than hardcoded launch limits unless platform operations later requires a temporary cap.',
    'Adult/sensitive galaxy membership inherits content rating and moderation metadata from ClassificationLayer and ModerationMetadata.',
    'Platform DSD+ entitlement reads, writes, downgrade sync, and gated enforcement are delegated to server-side Shopify tag verification and Supabase-derived entitlement state so Shopify remains the current-store authority.'
  ];

  function normalizeRole(role){
    var key = slug(role || 'member');
    return membershipRoles[key] ? key : 'member';
  }

  function normalizeState(state){
    var key = slug(state || 'active');
    return membershipStates[key] ? key : 'active';
  }

  function normalizeEntityType(type){
    var key = slug(type || 'galaxy');
    if (key === 'chat') key = 'conversation';
    if (key === 'story') key = 'storyGroup';
    return entityTypes[key] ? key : 'galaxy';
  }

  function canRoleAssign(assignerRole, targetRole){
    var a = membershipRoles[normalizeRole(assignerRole)] || membershipRoles.member;
    var t = membershipRoles[normalizeRole(targetRole)] || membershipRoles.member;
    return a.rank > t.rank && a.rank >= membershipRoles.admin.rank;
  }

  function canRoleModerate(role){
    var key = normalizeRole(role);
    return key === 'founder' || key === 'admin' || key === 'moderator';
  }

  function canRoleCreateChild(parentType, role, childType, settings){
    parentType = normalizeEntityType(parentType);
    childType = normalizeEntityType(childType);
    var roleKey = normalizeRole(role);
    settings = settings || {};
    var authority = childCreationAuthority[parentType] || {};
    if (authority.canCreateChildTypes && authority.canCreateChildTypes.indexOf(childType) === -1) return false;
    if (roleKey === 'founder' || roleKey === 'admin') return true;
    if (parentType === 'universe' && childType === 'galaxy') {
      var mode = settings.galaxyCreationMode || authority.defaultGalaxyCreationMode || 'approvalRequired';
      if (mode === 'open') return true;
      if (mode === 'trustedMembers' && roleKey === 'trustedMember') return true;
      if (mode === 'adminsOnly' || mode === 'approvalRequired' || mode === 'disabled') return false;
    }
    if ((childType === 'event' || childType === 'storyGroup') && roleKey === 'moderator') return true;
    return false;
  }

  function normalizeMembership(input, context){
    input = input || {};
    context = context || {};
    var entityType = normalizeEntityType(input.entityType || context.entityType);
    var role = normalizeRole(input.role || context.role);
    var state = normalizeState(input.state || context.state);
    var entityId = input.entityId || input.universeId || input.galaxyId || input.eventId || context.entityId || null;
    var userId = input.userId || input.uid || input.memberId || context.userId || null;
    var scopes = {
      universeScopes: unique([].concat(input.universeScopes || [], input.universes || [], input.universeId || [], context.universeScopes || [])),
      galaxyScopes: unique([].concat(input.galaxyScopes || [], input.galaxies || [], input.galaxyId || [], context.galaxyScopes || [])),
      eventScopes: unique([].concat(input.eventScopes || [], input.events || [], input.eventId || [], context.eventScopes || []))
    };
    var permissions = {
      canInvite: role === 'founder' || role === 'admin',
      canAssignAdmins: role === 'founder',
      canAssignModerators: role === 'founder' || role === 'admin',
      canModerate: canRoleModerate(role),
      canCreateGalaxies: canRoleCreateChild(entityType, role, 'galaxy', input.settings || context.settings),
      canCreateEvents: canRoleCreateChild(entityType, role, 'event', input.settings || context.settings),
      canCreateChats: canRoleCreateChild(entityType, role, 'conversation', input.settings || context.settings),
      canCreateStories: canRoleCreateChild(entityType, role, 'storyGroup', input.settings || context.settings),
      canTransferOwnership: role === 'founder'
    };
    return {
      version: VERSION,
      membershipId: input.membershipId || [entityType, entityId || 'pending-entity', userId || 'pending-user'].join(':'),
      userId: userId,
      entityType: entityType,
      entityId: entityId,
      role: role,
      state: state,
      scopes: scopes,
      permissions: permissions,
      joinedAt: input.joinedAt || null,
      invitedBy: input.invitedBy || null,
      assignedBy: input.assignedBy || null,
      moderationState: input.moderationState || 'clean',
      contentRatingInherited: input.contentRatingInherited || context.contentRatingInherited || null,
      visibleInCounts: !!(membershipStates[state] && membershipStates[state].visibleInCounts),
      firebaseReadsEnabled: false,
      firebaseWritesEnabled: false,
      realtimeEnabled: 'supabase-entitlement',
      enforcementMode: 'delegated-to-shopify-tags-and-supabase-entitlement'
    };
  }

  function getReport(reason){
    return {
      version: VERSION,
      reason: reason || 'manual-report',
      owner: OWNER,
      entitlementGate: ENTITLEMENT_GATE,
      requiredDsdPlusTags: REQUIRED_DSD_PLUS_TAGS.slice(),
      firebaseReadsEnabled: false,
      firebaseWritesEnabled: false,
      realtimeEnabled: 'supabase-entitlement',
      inviteDeliveryEnabled: false,
      adminActionsEnabled: false,
      countMutationEnabled: false,
      enforcementEnabled: 'shopify-tags-derived-supabase-entitlement',
      doctrine: membershipDoctrine,
      entityTypes: entityTypes,
      membershipRoles: membershipRoles,
      membershipStates: membershipStates,
      creationModes: creationModes,
      childCreationAuthority: childCreationAuthority,
      pathContracts: {
        futureUniverseMembership: 'universes/{universeId}/members/{userId}',
        futureGalaxyMembership: 'galaxies/{galaxyId}/members/{userId}',
        futureEventMembership: 'events/{eventId}/members/{userId}',
        futureConversationMembership: 'conversations/{conversationId}/members/{userId}',
        futureUserMembershipIndex: 'users/{userId}/memberships/{membershipId}'
      },
      dependencyReadiness: {
        DSDRegistry: exists('DSDRegistry'),
        IdentityAuthority: exists('DSDIdentityAuthority'),
        SurfaceRegistry: exists('DSDSurfaceRegistry'),
        VisibilityLayer: exists('DSDVisibilityLayer'),
        ModerationMetadata: exists('DSDModerationMetadata'),
        ClassificationLayer: exists('DSDClassificationLayer'),
        FirebaseSocialSchema: false,
        RealtimeOwnershipAudit: exists('DSDRealtimeOwnershipAudit'),
        firebaseReadsEnabled: false,
        firebaseWritesEnabled: false,
        realtimeEnabled: 'supabase-entitlement'
      },
      nextGate: 'p11dz500-phase5-membership-governance-active'
    };
  }

  function publish(reason){
    var report = getReport(reason || 'publish');
    claim('membership-registry:doctrine', { doctrine: membershipDoctrine, count: membershipDoctrine.length });
    claim('membership-registry:entity-types', { entityTypes: entityTypes, count: Object.keys(entityTypes).length });
    claim('membership-registry:roles', { roles: membershipRoles, count: Object.keys(membershipRoles).length });
    claim('membership-registry:states', { states: membershipStates, count: Object.keys(membershipStates).length });
    claim('membership-registry:creation-modes', { creationModes: creationModes, count: Object.keys(creationModes).length });
    claim('membership-registry:child-creation-authority', childCreationAuthority);
    claim('membership-registry:path-contracts', report.pathContracts);
    claim('membership-registry:dependency-readiness', report.dependencyReadiness);
    claim('membership-registry:future-ecosystem-readiness', {
      multiverse: true,
      universes: true,
      galaxies: true,
      galaxyStories: true,
      galaxyChat: true,
      events: true,
      membershipCounters: 'future-derived-from-canonical-membership-records'
    });
    claim('membership-registry:entitlement-firewall', {
      firebaseReadsEnabled: false,
      firebaseWritesEnabled: false,
      realtimeEnabled: 'supabase-entitlement',
      inviteDeliveryEnabled: false,
      adminActionsEnabled: false,
      countMutationEnabled: false,
      enforcementEnabled: 'shopify-tags-derived-supabase-entitlement',
      requiredDsdPlusTags: REQUIRED_DSD_PLUS_TAGS.slice(),
      gate: ENTITLEMENT_GATE
    });
    try { window.dispatchEvent(new CustomEvent('dsd:membership-registry-ready', { detail: report })); } catch(e) {}
    window.DSDMembershipRegistryReport = report;
    return report;
  }

  window.DSDMembershipRegistry = {
    version: VERSION,
    owner: OWNER,
    getReport: getReport,
    publish: publish,
    entityTypes: entityTypes,
    membershipRoles: membershipRoles,
    membershipStates: membershipStates,
    creationModes: creationModes,
    childCreationAuthority: childCreationAuthority,
    requiredDsdPlusTags: REQUIRED_DSD_PLUS_TAGS.slice(),
    normalizeMembership: normalizeMembership,
    canRoleAssign: canRoleAssign,
    canRoleModerate: canRoleModerate,
    canRoleCreateChild: canRoleCreateChild,
    slug: slug
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function(){ publish('dom-ready'); }, { once: true });
  } else {
    publish('already-ready');
  }
})();
